import {
  AnimatePresence,
  domAnimation,
  LazyMotion,
  motion,
} from 'framer-motion';
import { GetStaticProps } from 'next';
import { Router } from 'next/router';
import React, { useEffect, useMemo } from 'react';

import DoubleColorHeadline from 'components/DoubleColorHeadline/DoubleColorHeadline';
import Filter from 'components/Filter/Filter';
import { redirectToPageUrl } from 'containers/InsightsPage/InsightsSection.utils';
import useColoredHader from 'hooks/coloredHeader';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { InsightsArticlesTypes } from 'types/shared';
import { getArticleType } from 'utils/articles.utils';
import { queryCMS } from 'utils/cms/api';
import { insightsPageQuery } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import { DataCMSTypes, InsightsPageDataCMS } from './InsightsPage.types';

import * as S from './InsightsPage.styles';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.insightsPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS<DataCMSTypes>({
    query: insightsPageQuery({ id, locale, globalIds }),
  });

  const {
    data: {
      sharedCopies,
      insightsPage: page,
      redirectPopups,
      cookieBar,
      navbar,
      footer,
    },
  } = response;

  if (!page || !sharedCopies) return { notFound: true };

  const insights = page.articles.sort(
    (a: InsightsArticlesTypes, b: InsightsArticlesTypes) => {
      const articleA = a[getArticleType(a)];
      const articleB = b[getArticleType(b)];

      // @ts-ignore
      return new Date(articleB?.writingDate) - new Date(articleA?.writingDate);
    }
  );

  return {
    props: {
      initialCopy: {
        head: page?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      insights: { ...page, articles: insights },
    },
    revalidate: ISR_TIMEOUT,
  };
};

interface InsightsPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  insights: InsightsPageDataCMS;
  router: Router;
}

const InsightsPage: React.FunctionComponent<InsightsPageProps> = ({
  initialCopy,
  insights,
}) => {
  const { setCopy } = useCopyStore();
  const { activeFilter } = useGlobalStore();
  const { setHeaderText } = useGlobalStore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const articles = useMemo(() => {
    return !activeFilter
      ? insights?.articles
      : insights?.articles.filter(article => {
          return (
            article?.tag?.tag === activeFilter ||
            article?.tags?.some(t => t?.tag === activeFilter)
          );
        });
  }, [insights, activeFilter]);

  useColoredHader(insights.header);

  useEffect(() => {
    setCopy(initialCopy);
    setHeaderText({
      default: initialCopy?.navbar?.navLinks.find(
        ({ href }) => href === ROUTES.INSIGHTS
      )?.headerText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  if (!insights) return null;

  const onTileClick = (href, pageType) => {
    return redirectToPageUrl(href, pageType);
  };

  return (
    <S.Wrapper {...pageMotionProps}>
      <S.HeadlineWrapper>
        <DoubleColorHeadline
          size="heading1"
          headlines={insights.title?.headlines}
        />
      </S.HeadlineWrapper>

      <S.FilterContainer>
        <Filter
          filterText={insights?.buttonText}
          tagsList={insights?.filterPopup}
        />
      </S.FilterContainer>

      <S.InsightsList>
        <AnimatePresence initial={true}>
          {articles.map(article => (
            <LazyMotion features={domAnimation} key={article.id}>
              <motion.div viewport={{ once: true }}>
                <S.Link
                  href={onTileClick(
                    article.redirectTo,
                    article[getArticleType(article)]
                  )}
                >
                  <S.InsightsTile
                    {...article}
                    media={
                      (article.mediaType === 'image'
                        ? article.image
                        : article.video) || {
                        url: '',
                        alternativeText: '',
                      }
                    }
                    mediaType={article.mediaType}
                    videoErrorMessage={article.videoErrorMessage}
                    aspectRatio={article.aspectRatio}
                    videoType={{
                      autoplay: article.autoplay,
                      mute: article.mute,
                    }}
                    heading={article.heading}
                    description={article.description}
                    tag={article.tag?.tag}
                    cursorLabelRead={insights.cursorLabelRead}
                    writingDate={article[getArticleType(article)]?.writingDate}
                  />
                </S.Link>
              </motion.div>
            </LazyMotion>
          ))}
        </AnimatePresence>
      </S.InsightsList>
    </S.Wrapper>
  );
};

export default InsightsPage;
